import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { DeleteTwoTone } from '@ant-design/icons';
import {
  Button,
  Row,
  Col,
  Input,
  Checkbox,
  Typography,
  Radio,
  Tooltip,
  Form,
  type FormInstance,
} from 'antd';
import { Select, FormItemWrapper } from '../../form-components';
import { antdFormErrorEmpty, isNotValidNumber } from '../utils/formUtils';
import ProdConst from '../constants/productConstants';
import infoSVG from '../../assets/images/info.svg';
import minPriceOnImg from '../../assets/images/min-price-on.png';
import minPriceOffImg from '../../assets/images/min-price-off.png';
import customQtyOnImg from '../../assets/images/custom-qty-on.png';
import customQtyOffImg from '../../assets/images/custom-qty-off.png';
import { calcEstimatedPriceByCost } from '../utils/productUtils';
import { type PriceOption } from '../types';

// Extended PriceOption type for form usage with all fields
type FormPriceOption = {
  name?: string | null;
  uuid?: string;
  price?: number | null;
  cost?: number | null;
  use_custom_quantity?: boolean;
  custom_quantity_unit?: string | null;
  use_minimum_price?: boolean;
  minimum_price?: number | null;
  use_minimum_cost?: boolean;
  minimum_cost?: number | null;
  use_tiered_price?: boolean;
  tiered_price_method?: string;
  tiered_prices?:
    | {
        range_upper?: string | number | null;
        price?: string | number | null;
      }[]
    | undefined;
  order?: number;
  sku?: string | null;
};

const createTieredPriceItem = (
  rangeUpper: string | number | null,
  price: string | number | null
) => {
  return {
    range_upper: rangeUpper,
    price,
  };
};

type PriceOptionFormProps = {
  pricingMethodByCost: boolean;
  currencySymbol?: string;
  defaultGrossMargin?: number;
  option: PriceOption;
  onSubmit?: (values: PriceOption) => void;
};

type PriceOptionFormRef = {
  submitForm: () => Promise<void>;
  setValues: (values: FormPriceOption) => void;
  getFieldsError: () => any[];
  errors: any;
  values: FormPriceOption;
};

const PriceOptionForm = forwardRef<PriceOptionFormRef, PriceOptionFormProps>(
  (props, ref) => {
    const [form] = Form.useForm<FormPriceOption>();
    const { currencySymbol, defaultGrossMargin, pricingMethodByCost } = props;

    const useCustomQuantity = Form.useWatch('use_custom_quantity', form);
    const customQuantityUnit = Form.useWatch('custom_quantity_unit', form);
    const useMinimumPrice = Form.useWatch('use_minimum_price', form);
    const useTieredPrice = Form.useWatch('use_tiered_price', form);
    const cost = Form.useWatch('cost', form);
    const price = Form.useWatch('price', form);
    const tieredPriceMethod = Form.useWatch('tiered_price_method', form);
    const minimum_price = Form.useWatch('minimum_price', form);

    useEffect(() => {
      form.validateFields(['minimum_cost']);
    }, [minimum_price, form]);

    const sectionStyle = {
      backgroundColor: 'rgba(0,0,0,0.05)',
      width: '100%',
      padding: '5px 15px',
      borderRadius: '5px',
      marginBottom: '15px',
    };

    useEffect(() => {
      form.setFieldsValue(props.option as any);
    }, [props.option, form]);

    // Initialize tiered pricing when use_tiered_price becomes true
    useEffect(() => {
      if (useTieredPrice) {
        const currentValues = form.getFieldsValue();

        // Set default tiered_price_method if not set
        if (!currentValues.tiered_price_method) {
          form.setFieldValue('tiered_price_method', 'MULTIPLY');
        }

        // Initialize default tiered_prices if not set or empty
        if (
          !currentValues.tiered_prices ||
          currentValues.tiered_prices.length === 0
        ) {
          const defaultTieredPrices = [
            createTieredPriceItem(10, currentValues.price || null),
            createTieredPriceItem(null, currentValues.price || null),
          ];
          form.setFieldValue('tiered_prices', defaultTieredPrices);
        }
      }
    }, [useTieredPrice, form]);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: async () => {
          await form.validateFields();
          return form.submit();
        },
        setValues: (values: FormPriceOption) => {
          form.setFieldsValue(values as any);
        },
        getFieldsError: () => form.getFieldsError(),
        get errors() {
          const errors = form.getFieldsError();
          return antdFormErrorEmpty(errors) ? {} : errors;
        },
        get values() {
          return form.getFieldsValue();
        },
      }),
      [form]
    );

    const handleFinish = (values: FormPriceOption) => {
      if (props.onSubmit) {
        props.onSubmit(values as any);
      }
    };

    // Validation rules for minimum cost (shows error for both fields)
    const getMinimumCostRules = () => [
      {
        validator: (_: any, value: any) => {
          const formValues = form.getFieldsValue();
          if (formValues.use_minimum_price) {
            // Check if either field has been touched
            const costTouched = form.isFieldTouched('minimum_cost');
            const priceTouched = form.isFieldTouched('minimum_price');

            // Only validate after user has touched either input
            if (costTouched || priceTouched) {
              const hasValidMinimumCost = !isNotValidNumber(value);
              const hasValidMinimumPrice = !isNotValidNumber(
                formValues.minimum_price
              );

              // At least one field must be filled
              if (!hasValidMinimumCost && !hasValidMinimumPrice) {
                return Promise.reject(
                  new Error('Min. Total Cost or Min. Total Price is required')
                );
              }
            }
          }
          return Promise.resolve();
        },
      },
    ];

    return (
      <Form
        form={form}
        initialValues={props.option}
        onFinish={handleFinish}
        layout="vertical"
      >
        <div className="flex" style={sectionStyle}>
          <FormItemWrapper name="cost" label="Unit Cost">
            <Input addonBefore={currencySymbol} type="number" />
          </FormItemWrapper>
          <Form.Item
            name={pricingMethodByCost || useTieredPrice ? undefined : 'price'}
            label="Unit Price"
            className="ml-2"
          >
            <Input
              value={(() => {
                if (useTieredPrice) return 'Tiered Price';
                if (pricingMethodByCost) {
                  const calcEstimatedPrice = calcEstimatedPriceByCost({
                    cost: cost || 0,
                    defaultGrossMargin: defaultGrossMargin || 0,
                  });
                  return calcEstimatedPrice || undefined;
                }
                return price || undefined;
              })()}
              disabled={pricingMethodByCost || useTieredPrice}
              addonBefore={currencySymbol}
              type={useTieredPrice ? 'text' : 'number'}
            />
          </Form.Item>
        </div>

        <div style={sectionStyle}>
          <Form.Item
            name="use_tiered_price"
            valuePropName="checked"
            style={{ marginBottom: '5px' }}
          >
            <Checkbox>
              <div className="flex items-center">
                Use Tiered Price
                {pricingMethodByCost && (
                  <Tooltip title="When Tiered Price is enabled, Gross Margin of these products will be calculated base on Unit Cost and Tiered Price. Default Gross Margin will be ignored. ">
                    {/* <InfoIcon className="text-2xl" /> */}
                    <img src={infoSVG} height={16} width={16} alt="info" />
                  </Tooltip>
                )}
              </div>
            </Checkbox>
          </Form.Item>
          <Form.Item name="tiered_price_method" className="mb-2">
            <Radio.Group
              value={tieredPriceMethod || 'MULTIPLY'}
              disabled={!useTieredPrice}
              buttonStyle="solid"
              style={{
                width: '100%',
                textAlign: 'center',
              }}
            >
              <Radio.Button value="MULTIPLY" style={{ width: '40%' }}>
                Multiply
              </Radio.Button>
              <Radio.Button value="ADD_UP" style={{ width: '40%' }}>
                Add Up
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <TieredPriceList
            form={form}
            tieredPriceMethod={tieredPriceMethod || 'MULTIPLY'}
            currencySymbol={currencySymbol}
          />
        </div>

        <div style={sectionStyle}>
          <Row justify="space-between" align="middle" className="mb-0">
            <Col>
              <Form.Item
                name="use_minimum_price"
                valuePropName="checked"
                className="mb-0"
                style={{ marginBottom: '5px' }}
              >
                <Checkbox>
                  {pricingMethodByCost ? (
                    <div className="flex items-center">
                      Minimum Total
                      <Tooltip title="In a project, if the total cost of this product is smaller than the value below, use this value as total cost.">
                        {/* <InfoIcon className="text-2xl" /> */}
                        <img src={infoSVG} height={16} width={16} alt="info" />
                      </Tooltip>
                    </div>
                  ) : (
                    ' Minimum Total Price'
                  )}
                </Checkbox>
              </Form.Item>
            </Col>
            <Col>
              <img
                style={{ height: '16px' }}
                src={useMinimumPrice ? minPriceOnImg : minPriceOffImg}
                alt="minimum price indicator"
              />
            </Col>
          </Row>
          <div className="flex justify-between">
            <div className="flex-1">
              <Form.Item
                name="minimum_cost"
                label="Min. Total Cost"
                rules={getMinimumCostRules()}
                dependencies={['use_minimum_price']}
              >
                <Input
                  type="number"
                  addonBefore={currencySymbol}
                  disabled={!useMinimumPrice}
                />
              </Form.Item>
            </div>
            <div className="ml-2 flex-1">
              <Form.Item
                name="minimum_price"
                label={
                  <span className="flex items-center">
                    Min. Total Price
                    <Tooltip title="The default value is calculated base on min. total cost and default gross margin. If the total price of this option is smaller than minimum total price, use this value as total price.">
                      {/* <InfoIcon className="text-2xl ml-1" /> */}
                      <img src={infoSVG} height={16} width={16} alt="info" />
                    </Tooltip>
                  </span>
                }
              >
                <Input
                  type="number"
                  addonBefore={currencySymbol}
                  disabled={!useMinimumPrice}
                  // onChange={() => {
                  //   // Trigger validation on minimum_cost field when minimum_price changes
                  //   form.validateFields(['minimum_cost']);
                  // }}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        <div style={sectionStyle}>
          <Row justify="space-between" align="middle" className="mb-0">
            <Col>
              <Form.Item
                name="use_custom_quantity"
                valuePropName="checked"
                className="mb-0"
                style={{ marginBottom: '5px' }}
              >
                <Checkbox>Use Custom Quantity</Checkbox>
              </Form.Item>
            </Col>
            <Col>
              <img
                style={{ height: '16px' }}
                src={useCustomQuantity ? customQtyOnImg : customQtyOffImg}
                alt="custom quantity indicator"
              />
            </Col>
          </Row>
          <Row justify="space-between" align="middle">
            <Col span={6}>
              <Typography.Text>Custom Unit</Typography.Text>
            </Col>
            <Col span={18}>
              <Form.Item name="custom_quantity_unit" className="mb-0">
                <Select
                  disabled={!useCustomQuantity}
                  placeholder="Unit"
                  className="w-full"
                  style={{
                    fontStyle: customQuantityUnit === null ? 'italic' : 'unset',
                  }}
                >
                  <Select.Option
                    key={null}
                    value={null}
                    style={{ fontStyle: 'italic' }}
                  >
                    {' '}
                    ( empty ){' '}
                  </Select.Option>
                  {ProdConst.units.map(u => (
                    <Select.Option key={u.id} value={u.id}>
                      {u.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div style={sectionStyle}>
          <FormItemWrapper name="sku" label="SKU">
            <Input placeholder="SKU" />
          </FormItemWrapper>
        </div>
      </Form>
    );
  }
);

type TieredPriceListProps = {
  form: FormInstance;
  tieredPriceMethod: string;
  currencySymbol?: string;
};

const TieredPriceList = (props: TieredPriceListProps) => {
  const { currencySymbol } = props;
  const useTieredPrice = Form.useWatch('use_tiered_price', props.form);
  const handleAddTieredPriceRow = (add: any, fields: any[]) => {
    if (fields.length < 2) {
      return;
    }

    const formValues = props.form.getFieldsValue();
    const items = formValues.tiered_prices || [];
    const secondToLastItem = items[items.length - 2];
    const lastItem = items[items.length - 1];
    const newItem = createTieredPriceItem(
      secondToLastItem?.range_upper,
      lastItem?.price
    );

    add(newItem, items.length - 1);
  };

  const headerClassName = 'p-0 py-[2.5px] text-center';

  return (
    <div>
      <Row
        gutter={5}
        align="middle"
        style={{ marginBottom: '5px' }}
        className="bg-[#d3d3d3]"
      >
        <Col span={2} className={headerClassName}>
          <label>Tier</label>
        </Col>
        <Col span={12} className={headerClassName}>
          <label>Range</label>
        </Col>
        <Col span={8} className={headerClassName}>
          <label>Price</label>
        </Col>
      </Row>
      <Form.List name="tiered_prices">
        {(fields, { add, remove }) => (
          <div>
            {useTieredPrice ? (
              <>
                {fields.map((field, index) => (
                  <TieredPriceItem
                    key={field.key}
                    form={props.form}
                    field={field}
                    tieredPriceMethod={props.tieredPriceMethod}
                    currencySymbol={currencySymbol}
                    onDelete={() => remove(field.name)}
                    index={index}
                    total={fields.length}
                  />
                ))}
              </>
            ) : null}
            <div
              style={{ textAlign: 'center', width: '100%', marginTop: '5px' }}
            >
              <Button
                type="dashed"
                disabled={!useTieredPrice}
                onClick={() => handleAddTieredPriceRow(add, fields)}
                style={{ display: 'inline-block' }}
              >
                + New Row
              </Button>
            </div>
          </div>
        )}
      </Form.List>
    </div>
  );
};

type TieredPriceItemProps = {
  form: FormInstance;
  field: any;
  tieredPriceMethod: string;
  currencySymbol?: string;
  index: number;
  total: number;
  onDelete: () => void;
};

const TieredPriceItem = (props: TieredPriceItemProps) => {
  const { field, index, total, onDelete, currencySymbol } = props;
  const isLast = total - 1 === index;
  const isPenultimate = total - 2 === index;

  // Validation rules for tiered price range upper
  const getTieredPriceRangeRules = () => [
    {
      validator: (_: any, value: any) => {
        const formValues = props.form.getFieldsValue();
        if (formValues.use_tiered_price && formValues.tiered_prices) {
          if (!isLast) {
            const isRangeUpperValid = (ru: string | null | undefined) =>
              ru !== null && ru !== undefined && ru !== '';

            if (!isRangeUpperValid(value)) {
              return Promise.reject(new Error('Range upper required'));
            }

            if (index > 0) {
              const prevRangeUpper =
                formValues.tiered_prices[index - 1]?.range_upper;
              if (
                isRangeUpperValid(prevRangeUpper) &&
                Number(value) <= Number(prevRangeUpper)
              ) {
                return Promise.reject(
                  new Error('Must be greater than previous')
                );
              }
            }
          }
        }
        return Promise.resolve();
      },
    },
  ];

  // Validation rules for tiered price
  const getTieredPricePriceRules = () => [
    {
      required: true,
      message: 'Price required',
    },
  ];

  return (
    <Row gutter={5}>
      <Col span={2}>
        <Typography.Text>{index + 1}</Typography.Text>
      </Col>
      <Col span={12}>
        {!isLast ? (
          <FormItemWrapper
            name={[field.name, 'range_upper']}
            rules={getTieredPriceRangeRules()}
          >
            <Input
              type="number"
              addonBefore={props.tieredPriceMethod === 'MULTIPLY' ? '<' : '≤'}
            />
          </FormItemWrapper>
        ) : (
          <Input disabled value="Other" style={{ textAlign: 'center' }} />
        )}
      </Col>
      <Col span={8}>
        <FormItemWrapper
          name={[field.name, 'price']}
          rules={getTieredPricePriceRules()}
        >
          <Input type="number" addonBefore={currencySymbol} />
        </FormItemWrapper>
      </Col>
      <Col span={2} style={{ textAlign: 'right' }}>
        {isPenultimate && total > 2 && (
          <a onClick={onDelete}>
            <DeleteTwoTone twoToneColor="red" />
          </a>
        )}
      </Col>
    </Row>
  );
};

export default PriceOptionForm;
export { createTieredPriceItem };
